// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  InvoiceAgingBucket,
  InvoiceSortProperty,
  invoiceListingSchema,
} from '@/billing/api/types';
import { InvoiceStatus } from '@/constants/invoice-status';
import { Sort } from '@/constants/sort';
import { z } from 'zod';

const invoicesResponseSchema = z.object({
  data: invoiceListingSchema,
  metadata: responseMetadataSchema,
});

export interface InvoiceFilter {
  pageSize: number;
  pageNumber?: number;
  sort?: Sort<InvoiceSortProperty>;
  search?: string;
  status?: InvoiceStatus;
  customerKeys?: string[];
  projectKeys?: string[];
  agingBuckets?: InvoiceAgingBucket[];
  invoiceDateStart?: string;
  invoiceDateEnd?: string;
  dueDateStart?: string;
  dueDateEnd?: string;
  amountMin?: number;
  amountMax?: number;
}

// Serializes the filters that affect the status summary cards — customer,
// project, both date ranges, amount range, and aging buckets. Produces a
// stable cache key that does not change when status, search, sort, or page
// change so summary-driven UI does not thrash on non-summary interactions.
export function serializeInvoiceSummaryScope(filter?: InvoiceFilter): string {
  if (!filter) {
    return '';
  }
  const params = new URLSearchParams();
  if (filter.customerKeys && filter.customerKeys.length > 0) {
    [...filter.customerKeys].sort().forEach((key) => {
      params.append('customerKey', key);
    });
  }
  if (filter.projectKeys && filter.projectKeys.length > 0) {
    [...filter.projectKeys].sort().forEach((key) => {
      params.append('projectKey', key);
    });
  }
  if (filter.agingBuckets && filter.agingBuckets.length > 0) {
    [...filter.agingBuckets].sort().forEach((bucket) => {
      params.append('agingBucket', bucket);
    });
  }
  if (filter.invoiceDateStart) {
    params.append('invoiceDateStart', filter.invoiceDateStart);
  }
  if (filter.invoiceDateEnd) {
    params.append('invoiceDateEnd', filter.invoiceDateEnd);
  }
  if (filter.dueDateStart) {
    params.append('dueDateStart', filter.dueDateStart);
  }
  if (filter.dueDateEnd) {
    params.append('dueDateEnd', filter.dueDateEnd);
  }
  if (filter.amountMin !== undefined) {
    params.append('amountMin', filter.amountMin.toString());
  }
  if (filter.amountMax !== undefined) {
    params.append('amountMax', filter.amountMax.toString());
  }
  return params.toString();
}

export function serializeInvoiceFilter(filter?: InvoiceFilter): string {
  if (!filter) {
    return '';
  }

  const params = new URLSearchParams();

  params.append('limit', filter.pageSize.toString());
  if (filter.pageNumber !== undefined) {
    params.append('page', filter.pageNumber.toString());
  }
  if (filter.sort) {
    params.append('sortBy', filter.sort.property);
    params.append('sortOrder', filter.sort.direction);
  }
  if (filter.search) {
    params.append('search', filter.search);
  }
  if (filter.status) {
    params.append('status', filter.status);
  }
  if (filter.customerKeys && filter.customerKeys.length > 0) {
    [...filter.customerKeys].sort().forEach((key) => {
      params.append('customerKey', key);
    });
  }
  if (filter.projectKeys && filter.projectKeys.length > 0) {
    [...filter.projectKeys].sort().forEach((key) => {
      params.append('projectKey', key);
    });
  }
  if (filter.agingBuckets && filter.agingBuckets.length > 0) {
    [...filter.agingBuckets].sort().forEach((bucket) => {
      params.append('agingBucket', bucket);
    });
  }
  if (filter.invoiceDateStart) {
    params.append('invoiceDateStart', filter.invoiceDateStart);
  }
  if (filter.invoiceDateEnd) {
    params.append('invoiceDateEnd', filter.invoiceDateEnd);
  }
  if (filter.dueDateStart) {
    params.append('dueDateStart', filter.dueDateStart);
  }
  if (filter.dueDateEnd) {
    params.append('dueDateEnd', filter.dueDateEnd);
  }
  if (filter.amountMin !== undefined) {
    params.append('amountMin', filter.amountMin.toString());
  }
  if (filter.amountMax !== undefined) {
    params.append('amountMax', filter.amountMax.toString());
  }

  return params.toString();
}

export async function getInvoices(filter: InvoiceFilter = { pageSize: 20 }) {
  const serializedFilter = serializeInvoiceFilter(filter);
  const url = serializedFilter
    ? `/v1/invoice?${serializedFilter}`
    : '/v1/invoice';
  const response = await api.get(url);

  const result = await parseResponse(invoicesResponseSchema, response.data, {
    schemaName: 'Invoices',
    endpoint: url,
  });

  return result.data;
}
